// ============================================================================
// Gmail — `users.messages.list` + inbox hydration.
//
// `GET /gmail/v1/users/me/messages` returns only `{ id, threadId }` pairs.
// To populate `EmailSummary` (from / subject / snippet / receivedAt) we have
// to round-trip each id through `users.messages.get?format=metadata`. To keep
// the list call snappy we batch the hydration 10 wide via `Promise.all`.
//
// Gmail's default query is "in:inbox" — operators can override via the
// capability's `query` arg (Gmail search syntax, e.g. `is:unread from:foo`).
// ============================================================================

import type { EmailSummary } from '../../capabilities/email'
import { GMAIL_API_BASE } from './oauth'
import {
  classifyGmailError,
  GmailError,
  parseRetryAfterMs,
  sanitizeGmailErrorMessage,
  type GmailMessagesListResponse,
  type GoogleErrorEnvelope,
} from './types'
import { parseAddressList, decodeHeaderValue } from './mime'

// ============================================================================
// Types
// ============================================================================

export interface ListGmailMessagesInput {
  accessToken: string
  pageToken?: string
  /** Default 50. Gmail hard-caps at 500 per page. */
  limit?: number
  /** Gmail search syntax — defaults to `in:inbox`. */
  query?: string
  fetchImpl?: typeof fetch
  apiBase?: string
  /**
   * Max concurrent `messages.get` calls during hydration. Defaults to 10.
   * Gmail's per-user concurrent quota is generous but not infinite; 10
   * keeps us well inside the standard `gmailRequestsPerMinutePerUser`
   * bucket.
   */
  hydrateConcurrency?: number
}

export interface ListGmailMessagesOutput {
  emails: EmailSummary[]
  nextPageToken?: string
}

// ============================================================================
// listGmailMessages — primary entry point (list + hydrate)
// ============================================================================

export async function listGmailMessages(
  args: ListGmailMessagesInput,
): Promise<ListGmailMessagesOutput> {
  assertAccessToken(args.accessToken)
  const fetchImpl = args.fetchImpl ?? fetch
  const apiBase = args.apiBase ?? GMAIL_API_BASE
  const concurrency = args.hydrateConcurrency ?? 10
  const limit = Math.max(1, Math.min(args.limit ?? 50, 500))
  const query = args.query ?? 'in:inbox'

  const listUrl = new URL(`${apiBase}/gmail/v1/users/me/messages`)
  listUrl.searchParams.set('maxResults', String(limit))
  if (args.pageToken) listUrl.searchParams.set('pageToken', args.pageToken)
  if (query) listUrl.searchParams.set('q', query)

  const listResp = await fetchImpl(listUrl.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${args.accessToken}`,
    },
  })
  if (!listResp.ok) {
    throw await errorFromResponse(listResp)
  }

  const listBody = (await listResp.json()) as GmailMessagesListResponse
  const ids = Array.isArray(listBody.messages)
    ? listBody.messages.map((m) => m.id)
    : []
  if (ids.length === 0) {
    return { emails: [], nextPageToken: listBody.nextPageToken }
  }

  // ── Batched hydration
  const emails: EmailSummary[] = []
  for (let i = 0; i < ids.length; i += concurrency) {
    const batch = ids.slice(i, i + concurrency)
    const results = await Promise.all(
      batch.map((id) => hydrateSummary(id, args.accessToken, fetchImpl, apiBase)),
    )
    for (const r of results) {
      if (r) emails.push(r)
    }
  }

  return {
    emails,
    nextPageToken: listBody.nextPageToken,
  }
}

// ============================================================================
// hydrateSummary — one `messages.get?format=metadata` call per id
// ============================================================================

async function hydrateSummary(
  messageId: string,
  accessToken: string,
  fetchImpl: typeof fetch,
  apiBase: string,
): Promise<EmailSummary | null> {
  const url = new URL(`${apiBase}/gmail/v1/users/me/messages/${encodeURIComponent(messageId)}`)
  url.searchParams.set('format', 'metadata')
  for (const h of ['From', 'To', 'Subject', 'Date']) {
    url.searchParams.append('metadataHeaders', h)
  }

  const resp = await fetchImpl(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!resp.ok) {
    // A single hydrate failure shouldn't abort the whole page — but a 401 is
    // a hard stop because the token is dead.
    if (resp.status === 401) {
      throw await errorFromResponse(resp)
    }
    // For 404 / 429 / 5xx on one message we drop it and continue. The page
    // will be short by one; the operator retries on the next cursor.
    return null
  }

  const body = (await resp.json()) as {
    id: string
    threadId: string
    labelIds?: string[]
    snippet?: string
    internalDate?: string
    payload?: { headers?: Array<{ name?: string; value?: string }> }
  }
  const headers = Array.isArray(body.payload?.headers) ? body.payload!.headers! : []
  const fromRaw = findHeader(headers, 'From') ?? ''
  const toRaw = findHeader(headers, 'To') ?? ''
  const subjectRaw = findHeader(headers, 'Subject') ?? ''

  const from = parseAddressList(fromRaw)[0] ?? { email: '' }
  const to = parseAddressList(toRaw)
  const subject = decodeHeaderValue(subjectRaw) ?? ''
  const receivedAt = body.internalDate
    ? new Date(Number(body.internalDate)).toISOString()
    : new Date(0).toISOString()

  const summary: EmailSummary = {
    messageId: body.id,
    threadId: body.threadId,
    from,
    to,
    subject,
    snippet: body.snippet ?? '',
    receivedAt,
    isUnread: body.labelIds?.includes('UNREAD') ?? false,
    labels: body.labelIds,
  }
  return summary
}

// ============================================================================
// Helpers (shared with send.ts pattern)
// ============================================================================

function findHeader(
  headers: Array<{ name?: string; value?: string }>,
  name: string,
): string | undefined {
  const target = name.toLowerCase()
  for (const h of headers) {
    if (typeof h?.name === 'string' && h.name.toLowerCase() === target && typeof h.value === 'string') {
      return h.value
    }
  }
  return undefined
}

async function errorFromResponse(response: Response): Promise<GmailError> {
  let envelope: GoogleErrorEnvelope | null = null
  let text = ''
  try {
    text = await response.text()
    envelope = text ? (JSON.parse(text) as GoogleErrorEnvelope) : null
  } catch {
    envelope = null
  }
  const retryAfterMs = parseRetryAfterMs(response.headers.get('Retry-After'))
  if (!envelope && text) {
    return new GmailError(
      response.status === 401 ? 'AUTH_FAILED' : 'PROVIDER_ERROR',
      sanitizeGmailErrorMessage(text).slice(0, 200),
      { httpStatus: response.status, retryAfterMs },
    )
  }
  return classifyGmailError(
    response.status,
    envelope?.error?.errors?.[0]?.reason,
    envelope,
    retryAfterMs,
  )
}

function assertAccessToken(accessToken: string | undefined | null): void {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new GmailError('INVALID_INPUT', 'accessToken is required')
  }
}
